<template>
  <div class="mb-3 flex max-w-[500px] flex-col rounded-xl border p-8">
    <div class="flex pb-6">
      <ProductImage class="w-1/2 max-w-[150px] shrink-0" :product-media="product?.cover?.media" />
      <div class="flex flex-col justify-center gap-1 pl-4">
        <Tag v-if="subscriptionInterval" class="self-start" color="highlight">
          <CommonLabel path="BWF/shop/productDetailPage/subscriptionProduct/subscriptionLabel" />
        </Tag>
        <span class="block">{{ product?.translated?.name }}</span>
        <p class="block text-sm text-medium-grey">
          <span v-if="subscriptionInterval">
            <CommonLabel
              path="BWF/shop/productDetailPage/subscriptionProduct/subscriptionIntervalDropdownLabel"
            />: {{ subscriptionInterval.interval }}&nbsp;{{ intervalUnitLabel }}
          </span>
          <CommonLabel
            v-else
            path="BWF/shop/productDetailPage/subscriptionProduct/singlePurchaseLabel"
          />
        </p>
        <ProductCrossSellingPrice
          class="text-base font-medium"
          :list-price="listPrice"
          :unit-price="unitPrice"
        />
      </div>
    </div>
    <AddToCartButton
      :product="product"
      class="btn btn-blue mt-auto w-auto max-w-max self-center"
      @close-cross-selling-modal="$emit('close-cross-selling-modal')"
      ><CommonLabel path="BWF/shop/productDetailPage/crossSelling/crossSellingCardBtn"
    /></AddToCartButton>
  </div>
</template>

<script setup>
import CommonLabel from "~/templates/components/CommonLabel.vue";
import ProductCrossSellingPrice from "~/templates/components/shop/product/ProductCrossSellingPrice.vue";
import AddToCartButton from "~/templates/partials/header/AddToCartButton.vue";
import ProductImage from "~/templates/elements/ProductImage.vue";
import { Tag } from "~/components/clarity-ui/Tag";
import {
  CTX_KEY_SUBSCRIPTION_ACTIVE,
  CTX_KEY_SUBSCRIPTION_ACTIVE_ID,
} from "~/composables/shop/useProductSubscriptionConfigurator";
const props = defineProps(["crossSellingProduct", "subscriptionIndex"]);
const { getLabels } = useCommonLabels();

defineEmits(["close-cross-selling-modal"]);

const { product } = useProduct(props.crossSellingProduct);

const subscriptionInterval = computed(() => {
  return product.value?.extensions?.subscription?.intervalConfigurations?.[props.subscriptionIndex];
});

/*
 * This "intercepts" the injection chain as x selling product should not be mixed with the UI State of Product on the PDP
 * x selling products are pinned to a certain subscription if the interval is active.
 * provided values will end up here which will effectively scope the x selling from the main product.
 *
 * more:
 * composables/shop/useProductSubscriptionConfigurator.ts:71
 *
 * thoughts:
 * consider moving this manual / unresponsive ref to a custom composable
 *
 * */
const subscriptionChooserActive = ref(!!subscriptionInterval.value);
provide(CTX_KEY_SUBSCRIPTION_ACTIVE, subscriptionChooserActive);
const localInterval = ref(subscriptionInterval.value?.intervalConfigurationId);
provide(CTX_KEY_SUBSCRIPTION_ACTIVE_ID, localInterval);

const listPrice = computed(() => {
  return subscriptionInterval.value
    ? subscriptionInterval.value?.subscriptionPrice?.listPrice?.price
    : product.value?.calculatedPrice?.listPrice?.price;
});

const unitPrice = computed(() => {
  return subscriptionInterval.value
    ? subscriptionInterval.value?.subscriptionPrice?.totalPrice
    : product.value?.calculatedPrice?.unitPrice;
});

const intervalLabels = getLabels("BWF/shop/account/subscriptions/contract", [
  "days",
  "weeks",
  "months",
  "years",
]);

const intervalUnitLabel = intervalLabels[`${subscriptionInterval.value?.intervalUnit}`];
</script>
